import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import AppLayout from '../components/AppLayout';
import { routes } from '../lib/routes';
import './AnaliseTecnica.css';

const STAFF = ['admin', 'gestor', 'analista'];

export default function AnaliseTecnica() {
  const { protocolo_id } = useParams();
  const queryClient = useQueryClient();
  const [parecer, setParecer] = useState('');
  const [conclusao, setConclusao] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);

  const { data: user } = useQuery('me', () => api.get('/usuarios/me').then((r) => r.data), { retry: false });

  const { data: documentos } = useQuery(['documentos', protocolo_id], () =>
    api.get(`/documentos/${protocolo_id}`).then((r) => r.data)
  );

  const { data: analises } = useQuery(['analises', protocolo_id], () =>
    api.get(`/analises/${protocolo_id}`).then((r) => r.data)
  );

  const documentoPdf = documentos?.find((d) => d.tipo === 'pdf');

  useEffect(() => {
    if (!documentoPdf?.id) return undefined;
    let revoked = false;
    let objectUrl;
    (async () => {
      try {
        const res = await api.get(`/documentos/file/${documentoPdf.id}`, { responseType: 'blob' });
        objectUrl = URL.createObjectURL(res.data);
        if (!revoked) setPdfUrl(objectUrl);
      } catch {
        if (!revoked) toast.error('Não foi possível carregar o PDF.');
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentoPdf?.id]);

  const mutation = useMutation(
    () => api.post('/analises', { protocolo_id, parecer, conclusao }).then((r) => r.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['analises', protocolo_id]);
        toast.success('Análise registada com sucesso.');
        setParecer('');
        setConclusao('');
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Erro ao guardar análise.');
      },
    }
  );

  if (user && !STAFF.includes(user.tipo)) {
    return <Navigate to={routes.dashboard} replace />;
  }

  const podeSalvar = Boolean(conclusao);
  const salvarTitle = !conclusao ? 'Selecione uma conclusão para poder guardar.' : undefined;

  return (
    <AppLayout title="Análise técnica" subtitle="Parecer e conclusão do processo.">
      <div className="analise-page">
        <div className="analise-grid">
          <section className="panel analise-panel" aria-labelledby="analise-pdf-titulo">
            <div className="analise-panel-head">
              <h2 id="analise-pdf-titulo">Documento PDF</h2>
            </div>
            <div className="analise-panel-body">
              {pdfUrl ? (
                <div className="analise-pdf-frame">
                  <iframe src={`${pdfUrl}#toolbar=1`} title="Visualização do PDF anexado" />
                </div>
              ) : documentoPdf ? (
                <p className="sispro-empty analise-empty-msg">Carregando documento…</p>
              ) : (
                <p className="sispro-empty analise-empty-msg">
                  Nenhum PDF anexado a este protocolo. Envie um arquivo PDF na ficha do protocolo para visualizar aqui.
                </p>
              )}
            </div>
          </section>

          <section className="panel analise-panel" aria-labelledby="analise-form-titulo">
            <div className="analise-panel-head">
              <h2 id="analise-form-titulo">Parecer técnico</h2>
            </div>
            <div className="analise-panel-body">
              <div className="analise-form-stack">
                <div className="analise-field">
                  <label htmlFor="analise-conclusao">Conclusão</label>
                  <select
                    id="analise-conclusao"
                    value={conclusao}
                    onChange={(e) => setConclusao(e.target.value)}
                  >
                    <option value="">Selecione a conclusão</option>
                    <option value="deferido">Deferido</option>
                    <option value="indeferido">Indeferido</option>
                    <option value="exigencia">Em exigência</option>
                  </select>
                </div>

                <div className="analise-field">
                  <label htmlFor="analise-parecer">Parecer</label>
                  <textarea
                    id="analise-parecer"
                    placeholder="Descreva o parecer técnico, fundamentos e eventuais exigências."
                    value={parecer}
                    onChange={(e) => setParecer(e.target.value)}
                  />
                </div>
              </div>

              <div className="analise-actions">
                <p className="analise-hint">A conclusão é obrigatória. O parecer pode ser complementado depois mediante nova análise.</p>
                <button
                  type="button"
                  className="sispro-btn sispro-btn--primary"
                  title={salvarTitle}
                  onClick={() => mutation.mutate()}
                  disabled={mutation.isLoading || !podeSalvar}
                >
                  {mutation.isLoading ? 'Salvando…' : 'Guardar análise'}
                </button>
              </div>

              <div className="analise-historico">
                <h3>Histórico</h3>
                <div className="analise-historico-list">
                  {analises?.length ? (
                    analises.map((a) => (
                      <div key={a.id} className="analise-historico-item">
                        <p className="analise-hist-conclusao">{a.conclusao || '—'}</p>
                        <p className="analise-hist-data">{new Date(a.created_at).toLocaleString('pt-BR')}</p>
                        {a.parecer ? <p className="analise-hist-data" style={{ marginTop: '0.5rem' }}>{a.parecer}</p> : null}
                      </div>
                    ))
                  ) : (
                    <p className="sispro-empty" style={{ margin: 0 }}>
                      Nenhum registo anterior.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
